import * as THREE from "three";

import type { FlowFormLookId } from "../viz/scenes/bioclast";

const LOOK_INDEX: Record<FlowFormLookId, number> = {
  "cellular-matter": 0,
  "neural-field": 1,
  "infinite-petri": 2,
  "coherent-fold": 3,
  "cymatic-current": 4,
  "scale-lattice": 5,
  "deep-field": 6,
  "ramsey-flow": 7,
};

export class FlowFormParticleSystemGL {
  readonly mesh: THREE.Mesh;
  readonly count: number;
  readonly texSize: number;

  private readonly targets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private previousIndex = 0;
  private currentIndex = 1;
  private writeIndex = 2;
  private readonly computeScene = new THREE.Scene();
  private readonly computeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly computeQuad: THREE.Mesh;
  private readonly initMaterial: THREE.ShaderMaterial;
  private readonly updateMaterial: THREE.ShaderMaterial;
  private readonly renderMaterial: THREE.ShaderMaterial;
  private previousStep = 1 / 60;

  private readonly timeUniform = { value: 0 };
  private readonly stepUniform = { value: 1 / 60 };
  private readonly previousStepUniform = { value: 1 / 60 };
  private readonly bassUniform = { value: 0 };
  private readonly midUniform = { value: 0 };
  private readonly trebleUniform = { value: 0 };
  private readonly levelUniform = { value: 0 };
  private readonly onsetUniform = { value: 0 };
  private readonly flowSpeedUniform = { value: 1 };
  private readonly intensityUniform = { value: 1 };
  private readonly saturationUniform = { value: 1 };
  private readonly hueUniform = { value: 148 };
  private readonly lookUniform = { value: 0 };
  private readonly radiusUniform = { value: 8 };

  constructor(private readonly renderer: THREE.WebGLRenderer, count: number) {
    this.count = Math.max(1, Math.floor(count));
    this.texSize = Math.ceil(Math.sqrt(this.count));
    const targetOptions: THREE.RenderTargetOptions = {
      type: THREE.FloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
    };
    this.targets = [
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOptions),
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOptions),
      new THREE.WebGLRenderTarget(this.texSize, this.texSize, targetOptions),
    ];
    this.initMaterial = this.createInitMaterial();
    this.updateMaterial = this.createUpdateMaterial();
    this.renderMaterial = this.createRenderMaterial();
    this.computeQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.initMaterial);
    this.computeScene.add(this.computeQuad);
    this.mesh = this.createBillboards();
  }

  init(): void {
    this.computeQuad.material = this.initMaterial;
    for (const target of this.targets) {
      this.renderer.setRenderTarget(target);
      this.renderer.render(this.computeScene, this.computeCamera);
    }
    this.renderer.setRenderTarget(null);
    this.syncRenderTextures();
  }

  step(dt: number, time: number): void {
    const step = THREE.MathUtils.clamp(Number.isFinite(dt) ? dt : 1 / 60, 1 / 120, 1 / 30);
    this.timeUniform.value = time;
    this.stepUniform.value = step;
    this.previousStepUniform.value = this.previousStep;
    this.updateMaterial.uniforms.uPositionPrevious.value = this.targets[this.previousIndex].texture;
    this.updateMaterial.uniforms.uPositionCurrent.value = this.targets[this.currentIndex].texture;
    this.computeQuad.material = this.updateMaterial;
    this.renderer.setRenderTarget(this.targets[this.writeIndex]);
    this.renderer.render(this.computeScene, this.computeCamera);
    this.renderer.setRenderTarget(null);

    const nextPrevious = this.currentIndex;
    const nextCurrent = this.writeIndex;
    const nextWrite = this.previousIndex;
    this.previousIndex = nextPrevious;
    this.currentIndex = nextCurrent;
    this.writeIndex = nextWrite;
    this.previousStep = step;
    this.syncRenderTextures();
  }

  setLook(look: FlowFormLookId): void {
    this.lookUniform.value = LOOK_INDEX[look];
  }

  setAudio(bass: number, mid: number, treble: number, level: number, onset: number): void {
    this.bassUniform.value = THREE.MathUtils.clamp(bass, 0, 1.5);
    this.midUniform.value = THREE.MathUtils.clamp(mid, 0, 1.5);
    this.trebleUniform.value = THREE.MathUtils.clamp(treble, 0, 1.5);
    this.levelUniform.value = THREE.MathUtils.clamp(level, 0, 1.5);
    this.onsetUniform.value = THREE.MathUtils.clamp(onset, 0, 1);
  }

  setVisual(flowSpeed: number, intensity: number, saturation: number, hue: number): void {
    this.flowSpeedUniform.value = THREE.MathUtils.clamp(flowSpeed, 0, 4);
    this.intensityUniform.value = THREE.MathUtils.clamp(intensity, 0, 4);
    this.saturationUniform.value = THREE.MathUtils.clamp(saturation, 0, 1);
    this.hueUniform.value = ((hue % 360) + 360) % 360;
  }

  setBaseRadius(radius: number): void {
    this.radiusUniform.value = Math.max(0.1, radius);
  }

  setViewportHeight(height: number): void {
    this.renderMaterial.uniforms.uViewportHeight.value = Math.max(1, height);
  }

  setPointSize(size: number): void {
    this.renderMaterial.uniforms.uPointSize.value = Math.max(0.001, size);
  }

  setAlpha(alpha: number): void {
    this.renderMaterial.uniforms.uBaseAlpha.value = THREE.MathUtils.clamp(alpha, 0.01, 1);
  }

  getSpatialDiagnostics(): {
    centroidRadius: number;
    spread: [number, number, number];
    radialMean: number;
    radialStdDev: number;
    occupiedCellRatio: number;
    maxCellShare: number;
  } {
    const pixels = new Float32Array(this.texSize * this.texSize * 4);
    this.renderer.readRenderTargetPixels(
      this.targets[this.currentIndex],
      0,
      0,
      this.texSize,
      this.texSize,
      pixels,
    );
    const cellCount = 16;
    const cells = new Uint32Array(cellCount ** 3);
    const stride = Math.max(1, Math.floor(this.count / 50_000));
    const baseRadius = this.radiusUniform.value;
    let samples = 0;
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    let sumXX = 0;
    let sumYY = 0;
    let sumZZ = 0;
    let radialSum = 0;
    let radialSquaredSum = 0;
    for (let index = 0; index < this.count; index += stride) {
      const offset = index * 4;
      const x = pixels[offset] / baseRadius;
      const y = pixels[offset + 1] / baseRadius;
      const z = pixels[offset + 2] / baseRadius;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
      const radius = Math.hypot(x, y, z);
      sumX += x;
      sumY += y;
      sumZ += z;
      sumXX += x * x;
      sumYY += y * y;
      sumZZ += z * z;
      radialSum += radius;
      radialSquaredSum += radius * radius;
      const cellX = Math.min(cellCount - 1, Math.max(0, Math.floor((x / 2.1 * 0.5 + 0.5) * cellCount)));
      const cellY = Math.min(cellCount - 1, Math.max(0, Math.floor((y / 2.1 * 0.5 + 0.5) * cellCount)));
      const cellZ = Math.min(cellCount - 1, Math.max(0, Math.floor((z / 2.1 * 0.5 + 0.5) * cellCount)));
      cells[(cellZ * cellCount + cellY) * cellCount + cellX] += 1;
      samples += 1;
    }
    const divisor = Math.max(1, samples);
    const meanX = sumX / divisor;
    const meanY = sumY / divisor;
    const meanZ = sumZ / divisor;
    const radialMean = radialSum / divisor;
    let occupiedCells = 0;
    let maximumCell = 0;
    for (const count of cells) {
      if (count > 0) occupiedCells += 1;
      maximumCell = Math.max(maximumCell, count);
    }
    return {
      centroidRadius: Math.hypot(meanX, meanY, meanZ),
      spread: [
        Math.sqrt(Math.max(0, sumXX / divisor - meanX * meanX)),
        Math.sqrt(Math.max(0, sumYY / divisor - meanY * meanY)),
        Math.sqrt(Math.max(0, sumZZ / divisor - meanZ * meanZ)),
      ],
      radialMean,
      radialStdDev: Math.sqrt(Math.max(0, radialSquaredSum / divisor - radialMean * radialMean)),
      occupiedCellRatio: occupiedCells / cells.length,
      maxCellShare: maximumCell / divisor,
    };
  }

  dispose(): void {
    for (const target of this.targets) target.dispose();
    this.initMaterial.dispose();
    this.updateMaterial.dispose();
    this.renderMaterial.dispose();
    this.computeQuad.geometry.dispose();
    this.mesh.geometry.dispose();
  }

  private syncRenderTextures(): void {
    this.renderMaterial.uniforms.uPositionCurrent.value = this.targets[this.currentIndex].texture;
    this.renderMaterial.uniforms.uPositionPrevious.value = this.targets[this.previousIndex].texture;
  }

  private commonSimulationShader(): string {
    return /* glsl */ `
      uint hashUint(uint value) {
        value ^= value >> 16u;
        value *= 0x7feb352du;
        value ^= value >> 15u;
        value *= 0x846ca68bu;
        value ^= value >> 16u;
        return value;
      }

      float hashIndex(uint index, uint salt) {
        return float(hashUint(index ^ salt)) * (1.0 / 4294967295.0);
      }

      vec3 particleSeed(uint index) {
        return vec3(
          hashIndex(index, 0x68bc21ebu),
          hashIndex(index, 0x02e5be93u),
          hashIndex(index, 0x967a889bu)
        );
      }

      float sphereDistance(vec3 p, float radius) {
        return length(p) - radius;
      }

      float torusDistance(vec3 p, float majorRadius, float minorRadius) {
        return length(vec2(length(p.xz) - majorRadius, p.y)) - minorRadius;
      }

      float repeatCentered(float value, float spacing) {
        return mod(value + spacing * 0.5, spacing) - spacing * 0.5;
      }

      float fieldDistance(vec3 p) {
        float radius = length(p);
        float morph = clamp(0.2 + uMid * 0.42 + uOnset * 0.3, 0.12, 0.88);
        if (uLook == 3) {
          return mix(sphereDistance(p, 1.18), torusDistance(p, 0.9, 0.32), morph);
        }

        float frequency = uLook == 0 ? 2.15 : uLook == 1 ? 1.75 : uLook == 2 ? 1.25 : uLook == 4 ? 1.4 : uLook == 5 ? 2.4 : uLook == 6 ? 1.15 : 1.9;
        float warp = uLook == 0 ? 0.7 : uLook == 1 ? 0.48 : uLook == 2 ? 0.58 : uLook == 4 ? 0.72 : uLook == 5 ? 0.38 : uLook == 6 ? 0.48 : 0.62;
        float boundaryRadius = uLook == 6 ? 1.55 : 1.38 + morph * 0.16;
        float boundary = radius - boundaryRadius;

        if (uLook == 6) {
          float strata = sin(radius * frequency * 4.2 + uTime * 0.16) * warp * 0.16;
          return radius - 1.42 - strata;
        }
        if (uLook == 0) {
          float gyroid = sin(p.x * frequency) * cos(p.y * frequency) + sin(p.y * frequency) * cos(p.z * frequency) + sin(p.z * frequency) * cos(p.x * frequency);
          return max(abs(gyroid) - (0.36 + warp * 0.08), boundary * 0.72);
        }
        if (uLook == 1) {
          float network = sin(p.x * frequency + uTime * 0.1) + sin(p.y * frequency * 1.31 - uTime * 0.07) + sin(p.z * frequency * 1.73 + uTime * 0.05);
          return max(abs(network) - (0.24 + warp * 0.06), boundary * 0.8);
        }
        if (uLook == 2) {
          float spacing = max(0.45, 2.4 / frequency);
          vec3 q = vec3(repeatCentered(p.x, spacing), repeatCentered(p.y, spacing), repeatCentered(p.z, spacing));
          float membrane = abs(length(q) - spacing * (0.28 + warp * 0.06)) - 0.055;
          return max(membrane, boundary * 0.75);
        }
        if (uLook == 4) {
          float azimuth = atan(p.z, p.x);
          float polar = acos(clamp(p.y / max(0.0001, radius), -1.0, 1.0));
          float nodal = sin(azimuth * 7.0 + uTime * 0.18) * sin(polar * 6.0);
          float target = 1.02 + morph * 0.2 + nodal * warp * 0.18;
          return radius - target;
        }
        if (uLook == 5) {
          vec3 wave = sin(p * frequency);
          float lines = min(length(wave.xy), min(length(wave.yz), length(wave.zx)));
          return max(lines / frequency - (0.055 + warp * 0.018), boundary * 0.78);
        }
        float ramsey = sin(p.x * frequency + uTime * 0.08) * cos(p.y * frequency * 1.618) + sin(p.y * frequency * 1.414 - uTime * 0.07) * cos(p.z * frequency) + sin(p.z * frequency * 1.732 + uTime * 0.05) * cos(p.x * frequency * 1.272);
        return max(abs(ramsey) - (0.42 + warp * 0.08), boundary * 0.72);
      }

      vec3 fieldGradient(vec3 p) {
        float epsilon = 0.006;
        return normalize(vec3(
          fieldDistance(p + vec3(epsilon, 0.0, 0.0)) - fieldDistance(p - vec3(epsilon, 0.0, 0.0)),
          fieldDistance(p + vec3(0.0, epsilon, 0.0)) - fieldDistance(p - vec3(0.0, epsilon, 0.0)),
          fieldDistance(p + vec3(0.0, 0.0, epsilon)) - fieldDistance(p - vec3(0.0, 0.0, epsilon))
        ) + vec3(0.000001));
      }

      vec3 advection(vec3 p, vec3 seed) {
        float frequency = (1.15 + uTreble * 0.55) * (uLook == 0 ? 2.15 : uLook == 1 ? 1.75 : uLook == 2 ? 1.25 : uLook == 4 ? 1.4 : uLook == 5 ? 2.4 : uLook == 6 ? 1.15 : uLook == 7 ? 1.9 : 1.0);
        float phase = uTime * (0.18 + uFlowSpeed * 0.62 + uMid * 0.34);
        vec3 force;
        if (uLook == 6) {
          force = vec3(sin(p.y * frequency * 0.55 + phase * 0.32 + seed.x * 6.283), cos(p.z * frequency * 0.4 - phase * 0.18 + seed.y * 6.283) * 0.5, 1.45 + sin(p.x * frequency * 0.35 + seed.z * 6.283) * 0.55);
        } else if (uLook == 0) {
          force = vec3(-p.z + sin(p.y * frequency + phase + seed.x * 6.283) * 0.7, sin((p.x + p.z) * frequency * 0.7 + seed.y * 6.283) * 0.45, p.x + cos(p.y * frequency - phase + seed.z * 6.283) * 0.7);
        } else if (uLook == 1) {
          force = vec3(sin(p.z * frequency + phase + seed.x * 6.283) * 0.34, 1.0 + cos(p.x * frequency * 0.5 + seed.y * 6.283) * 0.2, sin(p.x * frequency - phase * 0.7 + seed.z * 6.283) * 0.34);
        } else if (uLook == 2) {
          force = vec3(cos(p.y * frequency + seed.x * 6.283) - sin(p.z * frequency - phase), sin(p.z * frequency + seed.y * 6.283) - cos(p.x * frequency + phase * 0.5), cos(p.x * frequency + seed.z * 6.283) - sin(p.y * frequency - phase * 0.4));
        } else if (uLook == 4) {
          float radius = max(0.001, length(p));
          float wave = sin(radius * frequency * 2.2 - phase * 2.0 + seed.x * 6.283);
          force = vec3(p.x / radius * wave - p.z / radius * 0.45, p.y / radius * wave * 0.5 + cos(phase + seed.y * 6.283) * 0.16, p.z / radius * wave + p.x / radius * 0.45);
        } else if (uLook == 5) {
          force = vec3(sin(p.y * frequency + phase + seed.x * 6.283), sin(p.z * frequency + phase * 0.73 + seed.y * 6.283), sin(p.x * frequency - phase * 0.61 + seed.z * 6.283));
        } else if (uLook == 7) {
          force = vec3(sin(p.y * frequency + phase) + cos(p.z * frequency * 1.618 + seed.x * 6.283), sin(p.z * frequency * 1.414 - phase * 0.7) + cos(p.x * frequency + seed.y * 6.283), sin(p.x * frequency * 1.732 + phase * 0.43) + cos(p.y * frequency + seed.z * 6.283));
        } else {
          force = vec3(sin(p.y * frequency + phase + seed.x * 6.283) - cos(p.z * frequency - phase * 0.73 + seed.y * 6.283), sin(p.z * frequency + phase * 0.61 + seed.y * 6.283) - cos(p.x * frequency + phase + seed.z * 6.283), sin(p.x * frequency - phase * 0.47 + seed.z * 6.283) - cos(p.y * frequency + phase * 0.83 + seed.x * 6.283));
        }
        float magnitude = max(1.0, length(force));
        return force / magnitude * (0.2 + uFlowSpeed * 0.18 + uLevel * 0.36);
      }

    `;
  }

  private createInitMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: `out vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: `
        precision highp float;
        in vec2 vUv;
        out vec4 outColor;
        uniform float uSize;
        uniform float uCount;
        uniform float uBaseRadius;
        uint hashUint(uint value) {
          value ^= value >> 16u;
          value *= 0x7feb352du;
          value ^= value >> 15u;
          value *= 0x846ca68bu;
          value ^= value >> 16u;
          return value;
        }
        float hashIndex(uint index, uint salt) {
          return float(hashUint(index ^ salt)) * (1.0 / 4294967295.0);
        }
        void main() {
          float px = floor(vUv.x * uSize);
          float py = floor(vUv.y * uSize);
          float index = py * uSize + px;
          if (index >= uCount) { outColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
          uint particleIndex = uint(index);
          float cosine = hashIndex(particleIndex, 0x68bc21ebu) * 2.0 - 1.0;
          float sine = sqrt(max(0.0, 1.0 - cosine * cosine));
          float azimuth = hashIndex(particleIndex, 0x02e5be93u) * 6.2831853;
          float radius = 0.35 + pow(hashIndex(particleIndex, 0x967a889bu), 0.333333) * 1.3;
          vec3 point = vec3(cos(azimuth) * sine, cosine, sin(azimuth) * sine) * radius * uBaseRadius;
          outColor = vec4(point, 1.0);
        }
      `,
      uniforms: {
        uSize: { value: this.texSize },
        uCount: { value: this.count },
        uBaseRadius: this.radiusUniform,
      },
    });
  }

  private createUpdateMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: `out vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`,
      fragmentShader: `
        precision highp float;
        in vec2 vUv;
        out vec4 outColor;
        uniform sampler2D uPositionPrevious;
        uniform sampler2D uPositionCurrent;
        uniform float uSize;
        uniform float uCount;
        uniform float uBaseRadius;
        uniform float uTime;
        uniform float uStep;
        uniform float uPreviousStep;
        uniform float uBass;
        uniform float uMid;
        uniform float uTreble;
        uniform float uLevel;
        uniform float uOnset;
        uniform float uFlowSpeed;
        uniform int uLook;
        ${this.commonSimulationShader()}
        void main() {
          float px = floor(vUv.x * uSize);
          float py = floor(vUv.y * uSize);
          float index = py * uSize + px;
          if (index >= uCount) { outColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
          uint particleIndex = uint(index);
          vec3 previous = texture(uPositionPrevious, vUv).rgb / uBaseRadius;
          vec3 current = texture(uPositionCurrent, vUv).rgb / uBaseRadius;
          vec3 velocity = (current - previous) / max(0.001, uPreviousStep);
          vec3 seed = particleSeed(particleIndex);
          vec3 seedDirection = normalize(seed * 2.0 - 1.0 + vec3(0.001));
          float thickness = uLook == 0 ? 0.28 : uLook == 1 ? 0.24 : uLook == 2 ? 0.32 : uLook == 3 ? 0.22 : uLook == 4 ? 0.24 : uLook == 5 ? 0.16 : uLook == 6 ? 0.42 : 0.24;
          float stratum = (hashIndex(particleIndex, 0xb5297a4du) * 2.0 - 1.0) * thickness;
          stratum += sin(uTime * 0.11 + seed.x * 6.283) * thickness * 0.1;
          float domainSpread = uLook == 5 ? 0.24 : uLook == 1 ? 0.42 : uLook == 6 ? 0.58 : 0.38;
          vec3 fieldPosition = current + seedDirection * domainSpread;
          float distance = fieldDistance(fieldPosition) - stratum;
          vec3 gradient = fieldGradient(fieldPosition);
          float formStrength = (uLook == 5 ? 2.35 : uLook == 3 ? 2.1 : uLook == 2 ? 2.0 : uLook == 0 ? 1.9 : uLook == 7 ? 1.85 : 1.65) * 0.68;
          float flowStrength = uLook == 6 ? 0.58 : uLook == 7 ? 0.5 : uLook == 4 ? 0.48 : uLook == 1 ? 0.5 : 0.34;
          float formWeight = clamp(0.88 - uBass * 0.28 - uOnset * 0.52, 0.2, 1.0);
          float attractionWidth = uLook == 6 ? 0.38 : uLook == 5 ? 0.24 : uLook == 1 ? 0.3 : 0.32;
          float formForce = -formStrength * formWeight * tanh(distance / attractionWidth);
          vec3 force = gradient * formForce + advection(current, seed) * flowStrength;
          vec3 laneTangent = cross(gradient, seedDirection);
          float laneLength = length(laneTangent);
          if (laneLength > 0.0001) laneTangent /= laneLength;
          vec3 laneBitangent = normalize(cross(gradient, laneTangent) + vec3(0.0001));
          float lanePhase = seed.y * 6.283 + uTime * (0.08 + uFlowSpeed * 0.06);
          force += laneTangent * (0.09 + uFlowSpeed * 0.038) * (0.72 + sin(lanePhase) * 0.28);
          force += laneBitangent * cos(lanePhase * 0.73 + seed.z * 4.0) * 0.052;
          float radius = max(0.0001, length(current));
          vec3 radial = current / radius;
          float targetRadius = mix(0.58, 1.86, seed.z);
          force += radial * clamp((targetRadius - radius) * 0.24, -0.18, 0.18);
          if (radius > 1.82) force -= radial * (radius - 1.82) * 4.0;
          float forceLength = length(force);
          if (forceLength > 4.0) force *= 4.0 / forceLength;
          velocity = (velocity + force * uStep) * exp(-(1.25 - uLevel * 0.18) * uStep);
          velocity += radial * uOnset * (0.2 + seed.z * 0.22);
          float speedLimit = 0.48 + uLevel * 0.28 + uBass * 0.14;
          float speed = length(velocity);
          if (speed > speedLimit) velocity *= speedLimit / speed;
          vec3 next = current + velocity * uStep;
          float nextRadius = length(next);
          if (nextRadius > 2.05) next *= 2.05 / nextRadius;
          outColor = vec4(next * uBaseRadius, 1.0);
        }
      `,
      uniforms: {
        uPositionPrevious: { value: null },
        uPositionCurrent: { value: null },
        uSize: { value: this.texSize },
        uCount: { value: this.count },
        uBaseRadius: this.radiusUniform,
        uTime: this.timeUniform,
        uStep: this.stepUniform,
        uPreviousStep: this.previousStepUniform,
        uBass: this.bassUniform,
        uMid: this.midUniform,
        uTreble: this.trebleUniform,
        uLevel: this.levelUniform,
        uOnset: this.onsetUniform,
        uFlowSpeed: this.flowSpeedUniform,
        uLook: this.lookUniform,
      },
    });
  }

  private createRenderMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.NormalBlending,
      vertexShader: `
        in vec2 aReference;
        uniform sampler2D uPositionCurrent;
        uniform sampler2D uPositionPrevious;
        uniform float uPointSize;
        uniform float uViewportHeight;
        uniform float uPreviousStep;
        uniform float uBaseRadius;
        uniform float uLevel;
        uniform float uOnset;
        out float vSpeed;
        out float vSeed;
        out float vRadius;
        out vec2 vSpriteUv;
        void main() {
          vec3 positionCurrent = texture(uPositionCurrent, aReference).rgb;
          vec3 positionPrevious = texture(uPositionPrevious, aReference).rgb;
          vec3 normalizedPosition = positionCurrent / uBaseRadius;
          vSpeed = length(positionCurrent - positionPrevious) / max(0.001, uPreviousStep * uBaseRadius);
          vSeed = fract(aReference.x * 73.13 + aReference.y * 191.71);
          vRadius = length(normalizedPosition);
          vec4 viewPosition = modelViewMatrix * vec4(positionCurrent, 1.0);
          vec2 viewVelocity = (modelViewMatrix * vec4(positionCurrent - positionPrevious, 0.0)).xy;
          vec2 tangent = normalize(viewVelocity + vec2(0.00001, 0.0));
          vec2 normal = vec2(-tangent.y, tangent.x);
          float worldSize = uPointSize * (7.0 + uLevel * 0.72 + uOnset * 0.9);
          float stretch = 1.0 + clamp(vSpeed * 1.65 + uOnset * 0.5, 0.0, 1.35);
          viewPosition.xy += tangent * position.x * worldSize * stretch +
            normal * position.y * worldSize;
          vSpriteUv = uv;
          gl_Position = projectionMatrix * viewPosition;
        }
      `,
      fragmentShader: `
        precision highp float;
        in float vSpeed;
        in float vSeed;
        in float vRadius;
        in vec2 vSpriteUv;
        out vec4 fragmentColor;
        uniform float uBaseAlpha;
        uniform float uBass;
        uniform float uMid;
        uniform float uTreble;
        uniform float uLevel;
        uniform float uOnset;
        uniform float uIntensity;
        uniform float uSaturation;
        uniform float uHue;
        uniform int uLook;
        vec3 rotateHue(vec3 color, float angle) {
          const mat3 toYiq = mat3(
            0.299, 0.587, 0.114,
            0.596, -0.274, -0.322,
            0.211, -0.523, 0.312
          );
          const mat3 toRgb = mat3(
            1.0, 0.956, 0.621,
            1.0, -0.272, -0.647,
            1.0, -1.106, 1.703
          );
          vec3 yiq = color * toYiq;
          float hue = atan(yiq.z, yiq.y) + angle;
          float chroma = length(yiq.yz);
          return clamp(vec3(yiq.x, chroma * cos(hue), chroma * sin(hue)) * toRgb, 0.0, 1.25);
        }
        vec3 hsl2rgb(vec3 hsl) {
          vec3 rgb = clamp(abs(mod(hsl.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
          return hsl.z + hsl.y * (rgb - 0.5) * (1.0 - abs(2.0 * hsl.z - 1.0));
        }
        float authoredHue() {
          if (uLook == 0) return 148.0;
          if (uLook == 1) return 196.0;
          if (uLook == 2) return 164.0;
          if (uLook == 3) return 174.0;
          if (uLook == 4) return 202.0;
          if (uLook == 5) return 178.0;
          if (uLook == 6) return 214.0;
          return 226.0;
        }
        float accentOffset() {
          if (uLook == 0) return 118.0;
          if (uLook == 1) return 72.0;
          if (uLook == 2) return -96.0;
          if (uLook == 3) return 132.0;
          if (uLook == 4) return 94.0;
          if (uLook == 5) return -122.0;
          if (uLook == 6) return 78.0;
          return 146.0;
        }
        vec3 lowColor() {
          if (uLook == 0) return vec3(0.03, 0.28, 0.18);
          if (uLook == 1) return vec3(0.02, 0.12, 0.38);
          if (uLook == 2) return vec3(0.02, 0.22, 0.28);
          if (uLook == 3) return vec3(0.05, 0.38, 0.60);
          if (uLook == 4) return vec3(0.02, 0.18, 0.50);
          if (uLook == 5) return vec3(0.02, 0.18, 0.34);
          if (uLook == 6) return vec3(0.01, 0.08, 0.28);
          return vec3(0.04, 0.12, 0.32);
        }
        vec3 middleColor() {
          if (uLook == 0) return vec3(0.08, 0.85, 0.46);
          if (uLook == 1) return vec3(0.05, 0.62, 0.78);
          if (uLook == 2) return vec3(0.16, 0.72, 0.58);
          if (uLook == 3) return vec3(0.16, 0.82, 0.58);
          if (uLook == 4) return vec3(0.06, 0.72, 0.82);
          if (uLook == 5) return vec3(0.08, 0.70, 0.64);
          if (uLook == 6) return vec3(0.04, 0.45, 0.70);
          return vec3(0.34, 0.66, 0.82);
        }
        vec3 highColor() {
          if (uLook == 0) return vec3(0.92, 0.68, 0.16);
          if (uLook == 1) return vec3(0.76, 0.88, 0.50);
          if (uLook == 2) return vec3(0.96, 0.42, 0.32);
          if (uLook == 3) return vec3(1.02, 0.48, 0.16);
          if (uLook == 4) return vec3(1.00, 0.76, 0.24);
          if (uLook == 5) return vec3(0.94, 0.82, 0.42);
          if (uLook == 6) return vec3(0.82, 0.62, 0.28);
          return vec3(1.00, 0.36, 0.20);
        }
        void main() {
          vec2 point = vSpriteUv * 2.0 - 1.0;
          float radius = length(point);
          if (radius > 1.0) discard;
          float pin = smoothstep(0.16, 0.0, radius);
          float core = smoothstep(0.5, 0.12, radius);
          float halo = smoothstep(0.76, 0.34, radius) * 0.025;
          float colorMix = clamp(vSeed * 0.7 + vRadius * 0.14 + vSpeed * 0.34, 0.0, 1.0);
          float baseHue = fract(uHue / 360.0);
          vec3 low = hsl2rgb(vec3(fract(baseHue - 0.045), 0.9, 0.2));
          vec3 middle = hsl2rgb(vec3(baseHue, 0.96, 0.48));
          vec3 high = hsl2rgb(vec3(fract(baseHue + accentOffset() / 360.0), 0.98, 0.58));
          vec3 color = mix(low, middle, smoothstep(0.08, 0.7, colorMix));
          color = mix(color, high, smoothstep(0.72, 1.0, colorMix + uTreble * 0.12));
          float accentHue = fract(baseHue + accentOffset() / 360.0 + vSeed * 0.62 + vRadius * 0.035);
          vec3 accent = hsl2rgb(vec3(accentHue, 0.96, 0.52));
          float accentMix = smoothstep(0.3, 0.82, fract(vSeed * 1.618 + vRadius * 0.37));
          color = mix(color, accent, accentMix * (0.62 + uTreble * 0.08));
          float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
          color = mix(vec3(luminance), color, uSaturation);
          float colorPeak = max(color.r, max(color.g, color.b));
          color /= max(1.0, colorPeak);
          float brightness = 0.62 + uIntensity * 0.25 + core * 0.35 + pin * 0.55 + uLevel * 0.16 + uOnset * 0.14 + vSpeed * 0.08;
          float alpha = (core * 0.96 + pin * 0.34 + halo) * uBaseAlpha * (0.92 + uBass * 0.1 + uMid * 0.07);
          fragmentColor = vec4(color * brightness, alpha);
        }
      `,
      uniforms: {
        uPositionCurrent: { value: null },
        uPositionPrevious: { value: null },
        uPointSize: { value: 0.04 },
        uViewportHeight: { value: 800 },
        uPreviousStep: this.previousStepUniform,
        uBaseRadius: this.radiusUniform,
        uBaseAlpha: { value: 0.45 },
        uBass: this.bassUniform,
        uMid: this.midUniform,
        uTreble: this.trebleUniform,
        uLevel: this.levelUniform,
        uOnset: this.onsetUniform,
        uIntensity: this.intensityUniform,
        uSaturation: this.saturationUniform,
        uHue: this.hueUniform,
        uLook: this.lookUniform,
      },
      side: THREE.DoubleSide,
    });
  }

  private createBillboards(): THREE.Mesh {
    const plane = new THREE.PlaneGeometry(1, 1);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.index = plane.index?.clone() ?? null;
    geometry.setAttribute("position", plane.getAttribute("position").clone());
    geometry.setAttribute("uv", plane.getAttribute("uv").clone());
    const references = new Float32Array(this.count * 2);
    const halfTexel = 0.5 / this.texSize;
    for (let index = 0; index < this.count; index += 1) {
      const x = index % this.texSize;
      const y = Math.floor(index / this.texSize);
      references[index * 2] = x / this.texSize + halfTexel;
      references[index * 2 + 1] = y / this.texSize + halfTexel;
    }
    geometry.setAttribute("aReference", new THREE.InstancedBufferAttribute(references, 2));
    geometry.instanceCount = this.count;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 100);
    plane.dispose();
    const mesh = new THREE.Mesh(geometry, this.renderMaterial);
    mesh.frustumCulled = false;
    return mesh;
  }
}

